import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useAuth } from '../../../middleware/authMiddleware';
import { 
  Home, 
  ChevronLeft, 
  ChevronRight, 
  FileText, 
  Calendar, 
  BookOpen, 
  Users, 
  Library,
  Settings,
  LogOut,
  HelpCircle
} from 'lucide-react';

const UserSidebar = ({ collapsed, toggleCollapse, permissions = [] }) => {
  const { logout, user } = useAuth();

  const navLinkClass = ({ isActive }) => 
    `flex items-center py-3 px-4 rounded-md transition-colors ${
      isActive 
        ? 'bg-primary-base text-white' 
        : 'text-gray-semidark hover:bg-gray-block'
    } ${collapsed ? 'justify-center' : ''}`;

  // Check if user has specific permissions
  const hasPermission = (permissionId) => {
    if (!permissions || !permissions.length) return false;
    return permissions.some(p => p === `edit_${permissionId}` || p === 'admin');
  };

  return (
    <div className="h-full bg-white border-r border-gray-divider flex flex-col">
      {/* Logo and collapse button */}
      <div className="flex items-center justify-between p-4 border-b border-gray-divider">
        <Link to="/user" className="flex items-center">
          {!collapsed ? (
            <span className="text-xl font-bold text-primary-base">NSS College</span>
          ) : (
            <span className="text-xl font-bold text-primary-base">NSS</span>
          )}
        </Link>
        
        <button 
          onClick={toggleCollapse}
          className="p-1 rounded-md hover:bg-gray-block text-gray-tertiary"
        >
          {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>

      {/* Navigation Links */}
      <div className="flex-1 py-4 px-2 overflow-y-auto">
        <nav>
          <ul className="space-y-2">
            <li>
              <NavLink to="/user" end className={navLinkClass}>
                <Home size={20} />
                {!collapsed && <span className="ml-3">Dashboard</span>}
              </NavLink>
            </li>

            {/* Show only if user has announcements permission */}
            {hasPermission('announcements') && (
              <li>
                <NavLink to="/user/announcements" className={navLinkClass}>
                  <FileText size={20} />
                  {!collapsed && <span className="ml-3">Announcements</span>}
                </NavLink>
              </li>
            )}

            {/* Show only if user has events permission */}
            {hasPermission('events') && (
              <li>
                <NavLink to="/user/events" className={navLinkClass}>
                  <Calendar size={20} />
                  {!collapsed && <span className="ml-3">Events</span>}
                </NavLink>
              </li>
            )}

            {/* Show only if user has courses permission */}
            {hasPermission('courses') && (
              <li>
                <NavLink to="/user/courses" className={navLinkClass}>
                  <BookOpen size={20} />
                  {!collapsed && <span className="ml-3">Courses</span>}
                </NavLink>
              </li>
            )}

            {/* Show only if user has faculty permission */}
            {hasPermission('faculty') && (
              <li>
                <NavLink to="/user/faculty" className={navLinkClass}>
                  <Users size={20} />
                  {!collapsed && <span className="ml-3">Faculty</span>}
                </NavLink>
              </li>
            )}

            {/* Show only if user has library permission */}
            {hasPermission('library') && (
              <li>
                <NavLink to="/user/library" className={navLinkClass}>
                  <Library size={20} />
                  {!collapsed && <span className="ml-3">Library</span>}
                </NavLink>
              </li>
            )}
          </ul>
        </nav>
      </div>

      {/* User Profile & Logout Section */}
      <div className="p-4 border-t border-gray-divider">
        <NavLink to="/user/help" className={navLinkClass}>
          <HelpCircle size={20} />
          {!collapsed && <span className="ml-3">Help</span>}
        </NavLink>
        <NavLink to="/user/settings" className={navLinkClass}>
          <Settings size={20} />
          {!collapsed && <span className="ml-3">Settings</span>}
        </NavLink>
        
        <button 
          onClick={logout}
          className={`mt-2 w-full flex items-center py-3 px-4 rounded-md transition-colors text-gray-semidark hover:bg-gray-block ${collapsed ? 'justify-center' : ''}`}
        >
          <LogOut size={20} />
          {!collapsed && <span className="ml-3">Logout</span>}
        </button>
        
        {!collapsed && user && (
          <div className="mt-4 pt-4 border-t border-gray-divider">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-primary-base text-white flex items-center justify-center font-medium">
                {user.fullName ? user.fullName.charAt(0).toUpperCase() : ''}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-dark truncate">
                  {user.fullName}
                </p>
                <p className="text-xs text-gray-tertiary truncate">
                  {user.email}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSidebar;